import React, { useState, useEffect } from 'react';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Switch, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress, 
  Box, 
  Checkbox, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid, 
  Chip 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import AlertIcon from '@mui/icons-material/Warning';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ProductDialog from './add.js';

const ProductList = () => {
  const { data: config } = useSelector((state) => state.config);
  const [products, setProducts] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductToEdit, setSelectedProductToEdit] = useState(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusProduct, setStatusProduct] = useState(null);
  const [productName, setProductName] = useState('');
  const [newStatus, setNewStatus] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchBrands();
    fetchCategories();
  }, []);
  
  const fetchBrands = async () => {
    try {
      const response = await apiClient.get(`${config.API_BASE_URL}/api/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(`${config.API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch products with populated brand and category
      const response = await apiClient.get(
        `${config.API_BASE_URL}/api/products/all`
      );
      
      // Products are already populated from the backend with brand and category names
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const filteredProducts = products.filter((product) => {
    const searchTermLower = searchTerm.toLowerCase();
    const nameMatch = product.name.toLowerCase().includes(searchTermLower);
    const idMatch = product._id.toLowerCase().includes(searchTermLower);
    
    // Apply brand filter
    const brandMatch = !selectedBrand || 
      (product.brand && product.brand._id === selectedBrand);
      
    // Apply category filter
    const categoryMatch = !selectedCategory || 
      (product.category && product.category._id === selectedCategory);
    
    return (nameMatch || idMatch) && brandMatch && categoryMatch;
  });
  
  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedCategory('');
    setSearchTerm('');
  };
  
  const hasActiveFilters = selectedBrand || selectedCategory || searchTerm;

  const handleAddDialogOpen = () => {
    setIsAddDialogOpen(true);
  };
  const handleAddProduct = () => {
    navigate('/products/add');
  };

  const handleAddDialogClose = () => {
    setIsAddDialogOpen(false);
  };
  const handleEditProduct = (product) => {
    navigate(`/products/edit/${product._id}`);
  };

  const handleProductAdded = (newProduct) => {
    setProducts((prevProducts) => [...prevProducts, newProduct]);
  };

  const handleEditDialogOpen = (product) => {
    setSelectedProductToEdit(product);
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setSelectedProductToEdit(null);
  };

  const handleStatusChange = async (productId, isActive) => {
    const product = products.find(p => p._id === productId);
    setStatusProduct(productId);
    setProductName(product?.name || '');
    setNewStatus(isActive);
    setIsStatusDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    setStatusLoading(true);
    try {
      await apiClient.put(`${config.API_BASE_URL}/api/products`, { productId: statusProduct, isActive: newStatus });
      const updatedProducts = products.map((product) => 
        product._id === statusProduct ? { ...product, isActive: newStatus } : product
      );
      setProducts(updatedProducts);
      setIsStatusDialogOpen(false);
    } catch (error) {
      console.error('Error updating product status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleProductUpdated = (updatedProduct) => {
    const updatedProducts = products.map((product) =>
      product._id === updatedProduct._id ? updatedProduct : product
    );
    setProducts(updatedProducts);
  };

  const openDetailsPage = (product) => {
    navigate(`/products/${product._id}`);
  };

  // Table container style with sticky header
  const tableContainerStyle = {
    overflowY: 'auto',
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: '200px', // Minimum height to ensure it's always visible
    position: 'relative',
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: '#1976d2',
      color: 'white',
    },
    '& tbody tr:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    }
  };

  return (
    <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2>Product List</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />} 
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <TextField
              size="small"
              placeholder="Search by name or ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 200 }}
            />
            <Button variant="contained" color="success" onClick={handleAddProduct}>
              Add New Product
            </Button>
          </div>
        </div>
        
        {showFilters && (
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '4px',
            marginBottom: '16px',
            border: '1px solid #e0e0e0'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    label="Brand"
                  >
                    <MenuItem value="">All Brands</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand._id} value={brand._id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={<ClearIcon />} 
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
        
        {hasActiveFilters && (
          <div style={{ marginBottom: '16px' }}>
            <span style={{ marginRight: '8px' }}>Active filters:</span>
            {selectedBrand && (
              <Chip 
                label={`Brand: ${brands.find(b => b._id === selectedBrand)?.name || selectedBrand}`} 
                onDelete={() => setSelectedBrand('')} 
                style={{ marginRight: '8px' }}
                size="small"
              />
            )}
            {selectedCategory && (
              <Chip 
                label={`Category: ${categories.find(c => c._id === selectedCategory)?.name || selectedCategory}`} 
                onDelete={() => setSelectedCategory('')} 
                style={{ marginRight: '8px' }}
                size="small"
              />
            )}
            {searchTerm && (
              <Chip 
                label={`Search: ${searchTerm}`} 
                onDelete={() => setSearchTerm('')} 
                style={{ marginRight: '8px' }}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      <div style={tableContainerStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ backgroundColor: '#1976d2', color: 'white' }}>
          <tr>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'center', 
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>S.No</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Product ID</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Name</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Brand</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Category</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'right',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Price</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'center',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Details</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              borderRight: '1px solid #fff', 
              textAlign: 'center',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Edit</th>
            <th style={{ 
              padding: '12px', 
              borderBottom: '1px solid #ddd', 
              textAlign: 'center',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#1976d2',
              color: 'white',
            }}>Active</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map((product, index) => (
            <tr key={product._id} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff' }}>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                {index + 1}
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd' }}>{product._id}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd' }}>{product.name}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd' }}>
                {product.brand?.name || 'N/A'}
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd' }}>
                {product.category?.name || 'N/A'}
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd', textAlign: 'right' }}>₹{product.price}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                <Button onClick={() => openDetailsPage(product)} startIcon={<InfoIcon />} size="small" variant="outlined" color="info">
                  Details
                </Button>
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                <Button onClick={() => handleEditProduct(product)} startIcon={<EditIcon />} size="small" variant="outlined" color="warning">
                  Edit
                </Button>
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                <Checkbox
                  checked={product.isActive}
                  onChange={(e) => handleStatusChange(product._id, e.target.checked)}
                  size="small"
                  disabled={statusLoading && product._id === statusProduct}
                  sx={{
                    '&.Mui-checked': {
                      color: '#4caf50', // Green when checked
                    },
                    '&.Mui-checked:hover': {
                      color: '#4caf50', // Keep green on hover
                    },
                    '&.Mui-checked.Mui-disabled': {
                      color: '#4caf50', // Keep green when disabled
                    },
                    '&:not(.Mui-checked)': {
                      color: '#f44336', // Red when unchecked
                    },
                    '&:not(.Mui-checked):hover': {
                      color: '#f44336', // Keep red on hover
                    },
                    '&:not(.Mui-checked).Mui-disabled': {
                      color: '#f44336', // Keep red when disabled
                    },
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Status Confirmation Dialog */}
      <Dialog open={isStatusDialogOpen} onClose={() => setIsStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {statusLoading ? 'Updating Status...' : 'Confirm Status Change'}
        </DialogTitle>
        <DialogContent>
          {statusLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <CircularProgress />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <AlertIcon color="warning" sx={{ fontSize: 40 }} />
              <div>
                <p>Are you sure you want to mark <span style={{ color: '#1976d2', fontWeight: 'bold' }}>{productName}</span> as <span style={{ color: newStatus ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>{newStatus ? 'Active' : 'Inactive'}</span>?</p>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsStatusDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={confirmStatusChange} variant="contained" color="success">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use the reusable ProductDialog for adding 
      <ProductDialog
        open={isAddDialogOpen}
        onClose={handleAddDialogClose}
        onProductAdded={handleProductAdded}
      />
*/}
      {/* Use the reusable ProductDialog for editing 
      {selectedProductToEdit && (
        <ProductDialog
          open={isEditDialogOpen}
          onClose={handleEditDialogClose}
          initialProduct={selectedProductToEdit}
          onProductUpdated={handleProductUpdated}
        />
      )}
        */}
    </div>
  );
};

export default ProductList;